<template>
  <div class="pt-panel pool-info-panel">
    <div class="pt-header">
      <div class="pt-header-text">
        <div class="pt-kicker">工程信息</div>
        <h2 class="pt-title">{{ oneTitle }}</h2>
        <p class="pt-subtitle">水池工程参数 · 设计指标 · 实时监测</p>
      </div>
      <button class="pt-close" @click="closeClick">&times;</button>
    </div>

    <div class="pt-tabs">
      <div
          v-for="(tab, idx) in tabs"
          :key="idx"
          class="pt-tab"
          :class="{ active: activeTab === tab }"
          @click="onTabChange(tab)"
      >
        {{ tab }}
      </div>
    </div>

    <!-- 工程参数 -->
    <div v-if="activeTab === '工程参数'" class="pt-flex-table">
      <div class="pt-flex-table-header">
        <div class="pt-flex-table-cell">项目</div>
        <div class="pt-flex-table-cell">内容</div>
      </div>
      <div
          class="pt-flex-table-row"
          v-for="(item, index) in parkInfo"
          :key="index"
      >
        <div class="pt-flex-table-cell">{{ item.label }}</div>
        <div class="pt-flex-table-cell">{{ item.value }}</div>
      </div>
    </div>

    <!-- 实时监测 -->
    <div v-if="activeTab === '实时监测'" class="ts-section">
      <div class="ts-metrics-row">
        <div class="ts-metric-card" v-for="(m, i) in tsMetrics" :key="'m'+i">
          <span class="ts-metric-label">{{ m.label }}</span>
          <div class="ts-metric-val-row">
            <span class="ts-metric-value" :class="m.cls">{{ m.value }}</span>
            <span class="ts-metric-unit">{{ m.unit }}</span>
          </div>
        </div>
      </div>

      <div class="ts-chart-group">
        <div class="ts-chart-card" v-for="(c, ci) in chartConfigs" :key="'c'+ci">
          <div class="ts-chart-title">{{ c.title }}</div>
          <div class="ts-chart-canvas" :ref="'chart' + ci"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import { generateReservoirTimeSeries } from '@/utils/reservoirTimeSeries.js'

export default {
  name: "poolInfo",
  props: {
    info: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      oneTitle: "",
      tabs: ["工程参数", "实时监测"],
      activeTab: "工程参数",
      parkInfo: [],
      tsData: null,
      tsMetrics: [],
      chartConfigs: [],
    };
  },

  created() {
    this.echartInstances = [];
  },

  mounted() {
    this.updateData();
    this._resizeHandler = () => {
      this.echartInstances.forEach(c => { try { c.resize(); } catch(e) {} });
    };
    window.addEventListener('resize', this._resizeHandler);
  },

  watch: {
    info: {
      handler(val) {
        this.oneTitle = val.name || "水池信息";
        this.updateData();
      },
      immediate: true,
    },
  },

  beforeDestroy() {
    this._destroyCharts();
    if (this._resizeHandler) window.removeEventListener('resize', this._resizeHandler);
  },

  methods: {
    onTabChange(tab) {
      this.activeTab = tab;
      if (tab === '实时监测') {
        this.$nextTick(() => {
          setTimeout(() => this._renderCharts(), 200);
        });
      }
    },

    updateData() {
      this.parkInfo = [
        { label: "主要任务", value: "农业灌溉、城镇供水及生态补水" },
        { label: "正常蓄水位", value: "615.20 m" },
        { label: "死水位", value: "610.50 m" },
        { label: "有效库容", value: "55 万 m³" },
        { label: "控制流域面积", value: "3.8 km²" },
        { label: "集水面积", value: "2.4 km²" },
        { label: "放水设施", value: "φ500 钢管放水涵" },
      ];

      // 用 generateReservoirTimeSeries 生成水池模拟数据
      const mockPool = {
        name: this.oneTitle || '调蓄水池',
        snapshot: { level: 613.5, inflow: 1.2, outflow: 1.0 },
        characteristic_levels: {
          normal_level: 615.2,
          dead_level: 610.5,
          flood_limit_level: 614.8,
        },
        capacities: { total_capacity: 0.0055 }, // 55 万 m³ = 0.0055 亿 m³
      };
      this.tsData = generateReservoirTimeSeries(mockPool);

      const ts = this.tsData;
      const lastIdx = ts.times.length - 1;
      const curLevel = ts.level[lastIdx];
      const curInflow = ts.inflow[lastIdx];
      const curOutflow = ts.outflow[lastIdx];
      const curStorage = ts.storage[lastIdx];
      const netFlow = curInflow - curOutflow;

      let levelStatus = '', levelCls = '';
      if (curLevel >= 615.2) { levelStatus = '正常高水位'; levelCls = 'st-green'; }
      else if (curLevel >= 614.0) { levelStatus = '正常'; levelCls = 'st-green'; }
      else if (curLevel >= 610.5) { levelStatus = '偏低'; levelCls = 'st-yellow'; }
      else { levelStatus = '低于死水位'; levelCls = 'st-red'; }

      this.tsMetrics = [
        { label: '当前水位', value: curLevel.toFixed(2), unit: 'm', cls: levelCls },
        { label: '入库流量', value: curInflow.toFixed(1), unit: 'm³/s', cls: '' },
        { label: '出库流量', value: curOutflow.toFixed(1), unit: 'm³/s', cls: '' },
        { label: '蓄水量', value: curStorage.toFixed(3), unit: '亿 m³', cls: '' },
      ];

      this.chartConfigs = [
        { title: '水位曲线 (m)', data: ts.level, color: '#38bdf8',
          markLine: { normal: 615.2, dead: 610.5 } },
        { title: '流量曲线 (m³/s)', data: ts.inflow, data2: ts.outflow,
          color: '#34d399', color2: '#fbbf24', name1: '入库', name2: '出库' },
        { title: '蓄水量 (亿 m³)', data: ts.storage, color: '#a78bfa', area: true },
      ];

      if (this.activeTab === '实时监测') {
        this.$nextTick(() => {
          setTimeout(() => this._renderCharts(), 200);
        });
      }
    },

    _renderCharts() {
      this._destroyCharts();
      if (!this.tsData || !this.chartConfigs.length) return;

      this.chartConfigs.forEach((cfg, idx) => {
        const refKey = 'chart' + idx;
        const el = this.$refs[refKey];
        if (!el || (Array.isArray(el) && el.length === 0)) return;
        const dom = Array.isArray(el) ? el[0] : el;
        const w = dom.offsetWidth || dom.clientWidth || 380;
        const h = 130;
        dom.style.width = w + 'px';
        dom.style.height = h + 'px';

        let chart;
        try {
          chart = echarts.init(dom, null, { width: w, height: h, renderer: 'canvas' });
        } catch(e) {
          console.error('[poolInfo] echarts.init error', idx, e);
          return;
        }
        this.echartInstances.push(chart);

        const series = [{
          name: cfg.name1 || cfg.title,
          type: 'line',
          data: cfg.data,
          smooth: true,
          symbol: 'none',
          lineStyle: { width: 2, color: cfg.color },
          itemStyle: { color: cfg.color },
          areaStyle: cfg.area ? {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: cfg.color + '40' },
              { offset: 1, color: cfg.color + '05' },
            ]),
          } : undefined,
        }];

        if (cfg.data2) {
          series.push({
            name: cfg.name2,
            type: 'line',
            data: cfg.data2,
            smooth: true,
            symbol: 'none',
            lineStyle: { width: 2, color: cfg.color2 },
            itemStyle: { color: cfg.color2 },
          });
        }

        const markLines = [];
        if (cfg.markLine) {
          if (cfg.markLine.normal) markLines.push({
            yAxis: cfg.markLine.normal, name: '正常蓄水位',
            lineStyle: { color: '#34d399', type: 'dashed', width: 1 },
            label: { show: true, formatter: '正常', color: '#34d399', fontSize: 9 },
          });
          if (cfg.markLine.dead) markLines.push({
            yAxis: cfg.markLine.dead, name: '死水位',
            lineStyle: { color: '#94a3b8', type: 'dashed', width: 1 },
            label: { show: true, formatter: '死水位', color: '#94a3b8', fontSize: 9 },
          });
        }

        chart.setOption({
          grid: { left: 50, right: 15, top: 25, bottom: 25 },
          tooltip: { trigger: 'axis', textStyle: { fontSize: 11 } },
          legend: cfg.data2 ? {
            show: true, data: [cfg.name1, cfg.name2],
            textStyle: { color: '#cbd5e1', fontSize: 10 }, top: 0, right: 5,
          } : { show: false },
          xAxis: {
            type: 'category',
            data: this.tsData.times,
            axisLabel: { color: '#94a3b8', fontSize: 9, interval: 23 },
            axisLine: { lineStyle: { color: 'rgba(148,163,184,0.3)' } },
          },
          yAxis: {
            type: 'value',
            scale: true,
            axisLabel: { color: '#94a3b8', fontSize: 9 },
            splitLine: { lineStyle: { color: 'rgba(148,163,184,0.1)' } },
          },
          series: series.map(s => {
            if (markLines.length > 0 && s === series[0]) {
              s.markLine = { silent: true, symbol: 'none', data: markLines };
            }
            return s;
          }),
        });
        setTimeout(() => { try { chart.resize(); } catch(e) {} }, 100);
      });
    },

    _destroyCharts() {
      this.echartInstances.forEach(c => { try { c.dispose(); } catch(e) {} });
      this.echartInstances = [];
    },

    closeClick() {
      this.$emit("infoClose");
    },
  },
};
</script>

<style scoped>
.pool-info-panel {
  width: 45%;
  height: 65%;
  position: absolute;
  overflow-y: auto;
  max-height: 65%;
  left: 30%;
  top: 200px;
}

/* === 实时监测区域 === */
.ts-section {
  padding: 0 2px;
}

.ts-metrics-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 14px;
}

.ts-metric-card {
  position: relative;
  padding: 8px 12px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.045);
  border: 1px solid rgba(255, 255, 255, 0.08);
  min-width: 100px;
}

.ts-metric-label {
  display: block;
  color: rgba(255, 255, 255, 0.52);
  font-size: 11px;
  margin-bottom: 4px;
}

.ts-metric-val-row {
  display: flex;
  align-items: baseline;
  gap: 3px;
}

.ts-metric-value {
  color: #fff;
  font-size: 18px;
  font-weight: 700;
}

.ts-metric-unit {
  color: rgba(125, 211, 252, 0.76);
  font-size: 10px;
}

.ts-chart-group {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.ts-chart-card {
  padding: 10px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.045);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.ts-chart-title {
  color: rgba(125, 211, 252, 0.76);
  font-size: 11px;
  margin-bottom: 4px;
}

.ts-chart-canvas {
  width: 100%;
  height: 130px;
  min-width: 300px;
  min-height: 130px;
}

/* === 状态颜色 === */
.st-red { color: #fca5a5; }
.st-yellow { color: #facc15; }
.st-green { color: #86efac; }
</style>
